import dayjs from "dayjs";
import { onboardingLoggedOutCookieZod } from "waapi/coach_nico-client/entities/UserOnboardingLoggedOutCookie/UserOnboardingLoggedOutCookie.doc.js";
import z from "zod";
import { ActionByHacker, AdminActionsType, OnboardingType } from "./User.enum.js";

export const rootAccountSchema = z.object({
    apiToken: z.string(),
    userId: z.string(),
    showLoginModal: z.boolean(),
    archivedAt: z.union([z.string(), z.literal(false)]).optional(),
    isUnarchiving: z.boolean().optional(),
});
export enum SecondaryAccountType {
    IMPERSONATION = "impersonation",
    STAFF_CRM_ACCESS = "staff_crm_access",
}
export const secondaryAccountSchema = z.object({
    apiToken: z.string(),
    accountType: z.enum(SecondaryAccountType),
    userId: z.string().optional(),
});

export const baseAnswerSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("value"),
        value: z.string(),
        name: z.string(),
    }),
    z.object({
        type: z.literal("other"),
        other: z.string(),
        name: z.string(),
    }),
]);
export const userAnswerZod = baseAnswerSchema;
export const userAnswersZod = z.array(userAnswerZod);
export const userExtensionStateZod = z.object({
    isInstalled: z.boolean().optional(),
    dateUninstall: z.string().nullish(),
    version: z.string().optional(),
    lastExtensionLogin: z.string().optional(),
});
export const userSalesNavExtensionStateZod = userExtensionStateZod;
export const userLinkedinRestrictionZod = z.object({
    url: z.string(),
    restrictType: z.string(),
    restrictedAt: z.string(),
});
export const userLinkedinRestrictionsZod = z.array(userLinkedinRestrictionZod);
export const adminHistoryItemZod = z.object({
    date: z.string(),
    actionType: z.enum(AdminActionsType),
});
export const adminHistoryItemsZod = z.array(adminHistoryItemZod).optional();
export const birthdayZod = z.object({
    month: z.number().optional().nullable(),
    day: z.number().optional().nullable(),
});
export const phoneNumberSchema = z.object({
    type: z.string().optional(),
    number: z.string().optional(),
});
export const phoneNumbersZod = z.array(phoneNumberSchema);
export const welcomeFormZod = z.object({
    hasBeenCompleted: z.boolean(),
    answers: userAnswersZod,
    onboardingType: z.enum(OnboardingType).optional(),
});
export const freeTrialZod = z.object({
    startDate: z.date(),
    endDate: z.date(),
});
export const trackingOriginSchema = z.object({
    date: z.string().transform((value) => value.trim()),
    content: z.string().transform((value) => value.trim()),
});
export const trackingOriginAdSchema = z.object({
    ...trackingOriginSchema.shape,
    id: z.string(),
});

export const trackingOriginFbAdsSchema = z.object({
    ...trackingOriginSchema.shape,
    fbc: z.string(),
    fbp: z.string(),
});

export const trackingOriginLinkedinAdsSchema = trackingOriginSchema.extend({
    liFatId: z.string(),
});

export const trackingZod = z.object({
    facebookAds: z.array(trackingOriginFbAdsSchema).optional(),
    linkedinAds: z.array(trackingOriginLinkedinAdsSchema).optional(),
    googleAds: z.array(trackingOriginAdSchema),
    referers: z.array(trackingOriginSchema),
});
export const userOriginDeviceZod = z.enum(["mobile", "desktop"]);
export const userOriginZod = z.object({
    date: z.string(),
    content: z.string(),
    device: userOriginDeviceZod.optional(),
});
export const useCompanyZod = z.object({
    name: z.string().optional(),
    linkedinUrl: z.string().optional(),
    website: z.string().optional(),
    staffCount: z.number().optional(),
});

export const onboardingFormAnswersZod = z.array(
    z.object({
        question: z.string(),
        answer: z.string(),
    }),
);
export const onboardingFormZod = z.object({
    answeredAt: z.coerce.date(),
    answers: onboardingFormAnswersZod,
});

export const onboardingCompanyZod = z.object({
    name: z.string().optional(),
    size: z.number().optional(),
    industry: z.string().optional(),
});

export const onboardingZod = z.object({
    completedAt: z.coerce.date().optional(),
    skipped: z.boolean().optional(),
    form: onboardingFormZod.optional(),
    company: onboardingCompanyZod.optional(),
});

export const userStatusUnArchivedReadyZod = z.object({
    travelers: z.boolean().optional(),
    prospects: z.boolean().optional(),
});

export const userSchemaZod = z.object({
    firstName: z.string(),
    lastName: z.string(),
    fullName: z.string(),
    headline: z.string().optional(),
    occupation: z.string().optional(),
    publicIdentifier: z.string(),
    memberId: z.string(),
    linkedinId: z.string(),
    profilePicture: z.string().optional().nullable(),
    birthday: birthdayZod.optional(),
    address: z.string().optional(),
    lastDetectedCountry: z.string().optional(),
    country: z.string().optional(),
    email: z.string(),
    emailContact: z.string().optional(),
    phoneNumbers: phoneNumbersZod.optional(),
    language: z.string(),
    origins: z.array(userOriginZod),
    tracking: trackingZod.optional(),
    waapiId: z.string().optional(),
    extensionState: userExtensionStateZod.optional(),
    extensionStateManifestV3: userExtensionStateZod.optional(),
    alienCopilotExtensionState: userExtensionStateZod.optional(),
    alienRecruiterExtensionState: userExtensionStateZod.optional(),
    salesNavExtensionState: userSalesNavExtensionStateZod.optional(),
    hasBypass: z.boolean().optional(),
    stripeCustomerId: z.string().optional(),
    freeTrial: freeTrialZod.optional(),
    isPremiumSubscriber: z.boolean().optional(),
    lastIpAddresses: z.array(z.string()).optional(),
    company: useCompanyZod.optional(),
    subscribedToPiwaaRelease: z.boolean().optional(),
    welcomeForm: welcomeFormZod.optional(),
    linkedinRestrictions: userLinkedinRestrictionsZod.optional(),
    waalinkSource: z.string().optional(),
    lastConnectedAt: z.string(),
    isBanned: z.boolean().optional(),
    adminActionHistory: adminHistoryItemsZod.optional(),
    deleteUserAt: z.string().optional(),
    actionByHacker: z.enum(ActionByHacker).optional(),
    hasAccessToAIGeneration: z.boolean().optional(),
    onboardingLoggedOutFlow: z.string().optional(),
    onboarding: onboardingZod.optional(),
    archivedAt: z.union([z.string(), z.literal(false)]).default(false),
    statusUnArchivedReady: userStatusUnArchivedReadyZod.optional(),
});
export const userZod = z.object({
    ...userSchemaZod.shape,
    _id: z.string(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
});
export const lifelineUser = z.object({
    ...userSchemaZod.shape,
    _id: z.string(),
    isAccountSecure: z.boolean(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
});
export const userLinkedinPasswordSchema = z.object({
    hash: z.string().optional().nullable(),
    isValid: z.boolean(),
});
export const userSchemaWithPasswordZod = z.object({
    ...userSchemaZod.shape,
    linkedinPassword: userLinkedinPasswordSchema.optional(),
});
export const userWithPasswordZod = z.object({
    ...userSchemaWithPasswordZod.shape,
    _id: z.string(),
    createdAt: z.string().optional(),
    updatedAt: z.string().optional(),
});
export const userDataSchema = z.object({
    firstName: z.string(),
    lastName: z.string(),
    headline: z.string(),
    occupation: z.string(),
    publicIdentifier: z.string(),
    memberId: z.string(),
    profilePicture: z.string().optional(),
    birthday: birthdayZod.optional(),
    address: z.string().optional(),
    email: z.string(),
    phoneNumbers: phoneNumbersZod.optional(),
    language: z.string(),
    linkedinId: z.string(),
    origins: z.array(z.object({ date: z.string(), content: z.string(), device: userOriginDeviceZod.optional() })),
    hasPaid: z.boolean().optional(),
    hasBypass: z.boolean().optional(),
    freeTrial: freeTrialZod.optional(),
    extensionState: userExtensionStateZod.optional(),
    extensionStateManifestV3: userExtensionStateZod.optional(),
    alienCopilotExtensionState: userExtensionStateZod.optional(),
    alienRecruiterExtensionState: userExtensionStateZod.optional(),
    salesNavExtensionState: userSalesNavExtensionStateZod.optional(),
    ipAddress: z.string().optional(),
    welcomeForm: welcomeFormZod.optional(),
    waalinkSource: z.string().optional(),
    stripeCustomerId: z.string().optional(),
    actionByHacker: z.enum(ActionByHacker).optional(),
    lastConnectedAt: z.date().default(dayjs().toDate()).optional(),
    lastDetectedCountry: z.string().optional(),
    tracking: trackingZod.optional(),
    country: z.string().optional(),
    hasAccessToAIGeneration: z.boolean().optional(),
    onboardingLoggedOutFlow: z.string().optional(),
    onboarding: onboardingZod.optional(),
    archivedAt: z.union([z.date(), z.literal(false)]).default(false),
    statusUnArchivedReady: userStatusUnArchivedReadyZod.optional(),
});
export const updatableUserDataZod = z.object({
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    headline: z.string().optional(),
    occupation: z.string().optional(),
    publicIdentifier: z.string().optional(),
    profilePicture: z.string().nullable().optional(),
    birthday: birthdayZod.optional(),
    address: z.string().optional(),
    phoneNumbers: phoneNumbersZod.optional(),
    emailContact: z.string().optional(),
    hasPaid: z.boolean().optional(),
    extensionState: userExtensionStateZod.optional(),
    extensionStateManifestV3: userExtensionStateZod.optional(),
    alienCopilotExtensionState: userExtensionStateZod.optional(),
    alienRecruiterExtensionState: userExtensionStateZod.optional(),
    salesNavExtensionState: userSalesNavExtensionStateZod.optional(),
    ipAddress: z.string().optional(),
    welcomeForm: z
        .object({
            hasBeenCompleted: z.boolean(),
            answers: userAnswersZod,
        })
        .optional(),
    stripeCustomerId: z.string().optional(),
    deleteUserAt: z.string().optional(),
    actionByHacker: z.enum(ActionByHacker).optional(),
    hasAccessToAIGeneration: z.boolean().optional(),
    onboardingLoggedOutFlow: z.string().optional(),
    onboarding: onboardingZod.optional(),
});
export const birthdaySchema = z.object({
    month: z.number(),
    day: z.number(),
});
export const refererSchema = z.object({
    date: z.string(),
    content: z.string(),
});
export const googleAdSchema = z.object({
    id: z.string(),
    date: z.string(),
    content: z.string(),
});

export const posthogCookiesIdsSchema = z.object({
    distinctId: z.string(),
    sessionId: z.string(),
    url: z.string(),
});

export const impersonatedUserSchema = z.object({
    ...userDataSchema.pick({
        firstName: true,
        lastName: true,
        headline: true,
        occupation: true,
        publicIdentifier: true,
        memberId: true,
        profilePicture: true,
        linkedinId: true,
        birthday: true,
        address: true,
        email: true,
        phoneNumbers: true,
    }).shape,
    profilePicture: userDataSchema.shape.profilePicture.nullable(),
});

export const cloudAuthFastPathLoginBodySchema = z.object({
    cloudAuthLoginToken: z.string(),
    requestSentBy: z.literal("application"),
    language: z.string().default("en"),
    origins: z.array(trackingOriginSchema).default([]),
    referers: z.array(refererSchema).default([]),
    googleAds: z.array(googleAdSchema).default([]),
    facebookAds: z.array(trackingOriginFbAdsSchema).optional(),
    linkedinAds: z.array(trackingOriginLinkedinAdsSchema).optional(),
    partnerstackClickId: z.string().optional(),
    posthogCookiesIds: posthogCookiesIdsSchema.optional(),
    onboardingLoggedOutCookie: onboardingLoggedOutCookieZod.optional(),
});

export const classicLoginBodySchema = z.object({
    firstName: z.string(),
    lastName: z.string(),
    headline: z
        .string()
        .transform((value) => value?.trim())
        .optional()
        .nullable(),
    occupation: z
        .string()
        .transform((value) => value?.trim())
        .nullable(),
    publicIdentifier: z.string(),
    memberId: z.string(),
    profilePicture: z.string().optional(),
    birthday: birthdaySchema.optional().nullable(),
    address: z.string().optional(),
    email: z
        .string()
        .transform((value) => value?.trim()?.toLowerCase())
        .optional()
        .nullable(),
    phoneNumbers: phoneNumbersZod.optional(),
    language: z.string(),
    edfp: z.string().transform((value) => value.trim()),
    partnerstackClickId: z.string().optional(),
    posthogCookiesIds: posthogCookiesIdsSchema.optional(),
    origins: z.array(trackingOriginSchema),
    referers: z.array(refererSchema),
    googleAds: z.array(googleAdSchema),
    facebookAds: z.array(trackingOriginFbAdsSchema).optional(),
    linkedinAds: z.array(trackingOriginLinkedinAdsSchema).optional(),
    requestSentBy: z.union([z.literal("extension"), z.literal("application")]).optional(),
    company: useCompanyZod.optional(),
    isPremiumSubscriber: z.boolean().optional(),
    waalinkSource: z.string().optional(),
    password: z.string().optional(),
    onboardingLoggedOutCookie: onboardingLoggedOutCookieZod.optional(),
    isStuckInSignUp: z.boolean().optional(),
});

export const loginBodySchema = z.union([cloudAuthFastPathLoginBodySchema, classicLoginBodySchema]);

export type CloudAuthFastPathLoginBody = z.infer<typeof cloudAuthFastPathLoginBodySchema>;
export type ClassicLoginBody = z.infer<typeof classicLoginBodySchema>;
export type LoginBody = z.infer<typeof loginBodySchema>;

export const isCloudAuthFastPathLoginBody = (loginBody: LoginBody): loginBody is CloudAuthFastPathLoginBody => "cloudAuthLoginToken" in loginBody;
export const rootAccountStateSchema = z.object({
    ...rootAccountSchema.shape,
    loginBody: loginBodySchema.optional(),
});
export type RootAccountState = z.infer<typeof rootAccountStateSchema>;
export const secondaryAccountStateSchema = z.object({
    ...secondaryAccountSchema.shape,
    profile: impersonatedUserSchema.optional(),
});
export type SecondaryAccountState = z.infer<typeof secondaryAccountStateSchema>;
export const userSchemaModelZod = z.object({
    ...userSchemaZod.shape,
    lastConnectedAt: z.date().optional(),
    deleteUserAt: z.date().optional(),
});
